import { Client, ClientRepresentationStatusEnum } from '@sr-sdks/api-sdk-axios';
import { Alert, Icon } from '@sr/ui-library';
import { useCallback, useEffect, useState } from 'react';
import { useEventCallback } from 'usehooks-ts';
import { useUpdateClient } from '../../../api/workspaces/clientApi';
import AppLoader from '../../../components/Loader';

/**
 * No bulk endpoint yet (APP-2174), so saves stay well under the shared
 * 100 req/s API Gateway throttle.
 */
export const SAVE_CONCURRENCY = 5;

/** Runs `task` over `items` with at most `limit` running at once. */
const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<R>,
) => {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex++;

      results[index] = await task(items[index]);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  );

  return results;
};

type SaveStatus = 'failed' | 'saved' | 'saving';

interface CompletedOnboardingStepProps {
  clientsList?: Client[];
  /** Runs once every update has settled, so a refetch reads the saved state. */
  onSaved?: () => void;
  /** Lets the modal block closing until the saves settle. */
  onSavingChange?: (isSaving: boolean) => void;
}

export const CompletedOnboardingStep = ({
  clientsList,
  onSaved,
  onSavingChange,
}: CompletedOnboardingStepProps) => {
  // `mutateAsync` is stable across renders, unlike the `useMutation` result
  // object, so it is safe as a dependency of `saveClients`.
  const { mutateAsync: updateClient } = useUpdateClient();
  // Stable, so a new callback from the parent never re-sends the updates.
  const handleSaved = useEventCallback(onSaved);
  const handleSavingChange = useEventCallback(onSavingChange);

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saving');
  const [failedClients, setFailedClients] = useState<Client[]>([]);

  const saveClients = useCallback(
    async (clients: Client[]) => {
      // Every client here was fetched as unconfirmed, so one still
      // unconfirmed is unchanged and saving it would be a no-op request.
      const decidedClients = clients.filter(
        ({ representationStatus }) =>
          representationStatus !== ClientRepresentationStatusEnum.Unconfirmed,
      );

      // Also covers the caller clearing the list on close, which must not
      // report the save as finished while an earlier one is still running.
      if (!decidedClients.length) {
        setSaveStatus('saved');

        return;
      }

      setSaveStatus('saving');
      handleSavingChange?.(true);

      const isSavedResults = await mapWithConcurrency(
        decidedClients,
        SAVE_CONCURRENCY,
        ({ _id: id, representationStatus }) =>
          // Inside the chain so a synchronous throw fails only this client,
          // rather than the whole run while the other saves are still landing.
          Promise.resolve()
            .then(() =>
              updateClient({
                id,
                updateClientDto: { representationStatus },
              }),
            )
            .then(
              () => true,
              () => false,
            ),
      );

      const unsavedClients = decidedClients.filter(
        (_, index) => !isSavedResults[index],
      );

      setFailedClients(unsavedClients);
      setSaveStatus(unsavedClients.length ? 'failed' : 'saved');
      handleSavingChange?.(false);
      // Even after a partial failure: the saves that landed still changed the
      // lists a refetch reads.
      handleSaved?.();
    },
    [updateClient, handleSaved, handleSavingChange],
  );

  useEffect(() => {
    if (!clientsList) return;

    saveClients(clientsList);
  }, [clientsList, saveClients]);

  if (saveStatus === 'saving') return <AppLoader.FlexLoader />;

  return (
    <div className="flex flex-col items-center justify-center gap-5 self-stretch py-5">
      {saveStatus === 'failed' ? (
        <Alert
          actionButtonProps={{
            children: 'Try Again',
            onClick: () => saveClients(failedClients),
          }}
          hasActionButton
          variant="error"
        >
          We couldn&apos;t save {failedClients.length} of your client decisions.
          Please try again. Any that still aren&apos;t saved will show as
          unconfirmed next time.
        </Alert>
      ) : (
        <div className="flex flex-col items-center justify-center gap-8 self-stretch">
          <Icon className="h-16 w-16 text-accent" icon="success" />
          <h2>All Done</h2>
          <p className="text-center text-sr-default">
            Thank you for taking the time to confirm your clients. You may now
            close this window and continue to the Screenrights portal.
          </p>
        </div>
      )}
    </div>
  );
};
